import { isEmpty, isNil, last } from "lodash";
import { asArray, intersects } from "./lodash-extra";
import type { nil } from "./type-utils";

/**
 * Values of the backend `EmailEventType` enum that decide whether a process mail went out. Plain strings
 * because the backend and the frontend each have their own enum (the frontend's comes from codegen), and
 * both pass their values in here.
 */
export const PROCESS_EMAIL_EVENT_TYPE = {
  Sent: "Sent",
  NotSent: "Not Sent",
  OptedOut: "Opted out",
  Resent: "Resent",
  Stopped: "Stopped",
} as const;

/**
 * GraphQL transports an enum by its member name, so the frontend sees these names where the backend has the
 * differing values of {@link PROCESS_EMAIL_EVENT_TYPE}. Every check has to accept both spellings.
 */
const PROCESS_EMAIL_EVENT_TYPE_GRAPHQL_NAME = {
  NotSent: "NotSent",
  OptedOut: "OptedOut",
} as const;

/** Values of the backend `EmailStopReason` enum, see {@link PROCESS_EMAIL_EVENT_TYPE} for why they are strings. */
export const PROCESS_EMAIL_STOP_REASON = {
  Manual: "Manual",
  TestProcess: "TestProcess",
  Skipped: "Skipped",
} as const;

const NOT_SENT_EVENT_TYPES: string[] = [
  PROCESS_EMAIL_EVENT_TYPE.Stopped,
  PROCESS_EMAIL_EVENT_TYPE.NotSent,
  PROCESS_EMAIL_EVENT_TYPE_GRAPHQL_NAME.NotSent,
];

const RESENDABLE_EVENT_TYPES: string[] = [
  ...NOT_SENT_EVENT_TYPES,
  PROCESS_EMAIL_EVENT_TYPE.OptedOut,
  PROCESS_EMAIL_EVENT_TYPE_GRAPHQL_NAME.OptedOut,
  PROCESS_EMAIL_EVENT_TYPE.Resent,
];

export interface ProcessEmailDispatchEvent {
  type: string;
  stopReason?: string;
}

export interface ProcessEmailDispatchEmail {
  organizationId?: string;
  responsibleRoles?: string[];
  targetRoles?: string[];
}

export interface ProcessEmailDispatchUser {
  organizationId: string;
  isCandidate: boolean;
  processRoles: string[];
  isProcessObserver: boolean;
}

export interface ProcessEmailDispatchCheck {
  email: ProcessEmailDispatchEmail;
  user: ProcessEmailDispatchUser;
}

export interface ResendableProcessEmail {
  processId?: string;
  events?: ProcessEmailDispatchEvent[];
}

/**
 * Whether a user may stop a pending process mail or send a not-sent one again (AB#22297). Same rule as
 * configuring or acting on the task the mail belongs to: a process role among the mail's responsible or
 * target roles, or a user of the mail's organization who is not merely observing the process.
 */
export function canControlProcessEmailDispatch({ email, user }: ProcessEmailDispatchCheck): boolean {
  if (user.isCandidate) {
    return false;
  }

  const emailRoles = [...asArray(email.responsibleRoles), ...asArray(email.targetRoles)];
  if (intersects(emailRoles, asArray(user.processRoles))) {
    return true;
  }

  const isOwnOrganization = email.organizationId === user.organizationId;
  const isObserverOnly = user.isProcessObserver && isEmpty(user.processRoles);
  return isOwnOrganization && !isObserverOnly;
}

/**
 * A process mail may be sent again only if it went out to nobody, so a partially sent mail never reaches a
 * recipient twice. A failed resend (`Resent` followed by `Not Sent`) can be retried. A mail stopped because
 * it belonged to a test process never can: it was generated from a model version nobody released.
 */
export function isResendableProcessEmail(email: ResendableProcessEmail): boolean {
  const events = asArray(email.events);
  const hasProcess = !isNil(email.processId);
  const wasNotSent = events.some(({ type }) => NOT_SENT_EVENT_TYPES.includes(type));
  const wasSentToNobody = events.every(({ type }) => RESENDABLE_EVENT_TYPES.includes(type));
  const isFromTestProcess = events.some(
    ({ type, stopReason }) =>
      type === PROCESS_EMAIL_EVENT_TYPE.Stopped && stopReason === PROCESS_EMAIL_STOP_REASON.TestProcess
  );
  return hasProcess && wasNotSent && wasSentToNobody && !isFromTestProcess;
}

/**
 * Why a mail was withheld, or `null` once it went out to anyone (a successful resend keeps the old `Stopped`
 * events next to its `Sent` ones).
 */
export function getEmailStopReason(events: ProcessEmailDispatchEvent[] | nil): string | null {
  const allEvents = asArray(events);
  if (allEvents.some(({ type }) => type === PROCESS_EMAIL_EVENT_TYPE.Sent)) {
    return null;
  }

  const lastStopEvent = last(allEvents.filter(({ type }) => type === PROCESS_EMAIL_EVENT_TYPE.Stopped));
  return lastStopEvent?.stopReason ?? null;
}
